// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import type { Catalog, Dispatch, SmartKeywordsSettings } from 'state/types';
import { getSmartKeywordsSettings } from 'state/selectors';
import {
  getCatalogProperties as getCatalogPropertiesCommand,
  updateSmartKeywordsSettings,
} from 'state/commands/catalogCommands';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  CATALOG_PROPERTY_KEY,
  CONFIDENCE_LEVEL,
  NUM_KEYWORDS,
} from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import SmartKeywordsEngineSettingsRow from './SmartKeywordsEngineSettingsRow';
import BulmaSlider from './BulmaSlider';

const SK_ENGINE_GENERAL = 'SK_ENGINE_GENERAL';
const SK_ENGINE_TRAVEL = 'SK_ENGINE_TRAVEL';
const SK_ENGINE_FOOD = 'SK_ENGINE_FOOD';
const SK_ENGINE_WEDDINGS = 'SK_ENGINE_WEDDINGS';
const SK_ENGINE_NSFW = 'SK_ENGINE_NSFW';
const SK_ENGINE_CELEBRITIES = 'SK_ENGINE_CELEBRITIES';
const SK_ENGINE_APPAREL = 'SK_ENGINE_APPAREL';
const SK_ENGINE_LOGO = 'SK_ENGINE_LOGO';
const SK_ENGINE_MODERATION = 'SK_ENGINE_MODERATION';
const SK_ENGINE_ART_STYLE = 'SK_ENGINE_ART_STYLE';
const SK_ENGINE_ART_MOVEMENT = 'SK_ENGINE_ART_MOVEMENT';
const SK_ENGINE_ART_SUBJECT = 'SK_ENGINE_ART_SUBJECT';

const skLanguages = [
  { locale: 'en', label: 'English' },
  { locale: 'fr', label: 'French' },
  { locale: 'de', label: 'German' },
  { locale: 'es', label: 'Spanish' },
  { locale: 'it', label: 'Italian' },
  { locale: 'ja', label: 'Japanese' },
  { locale: 'ar', label: 'Arabic' },
  { locale: 'bn', label: 'Bengali' },
  { locale: 'da', label: 'Danish' },
  { locale: 'fi', label: 'Finnish' },
  { locale: 'hi', label: 'Hindi' },
  { locale: 'hu', label: 'Hungarian' },
  { locale: 'ko', label: 'Korean' },
  { locale: 'nl', label: 'Dutch' },
  { locale: 'no', label: 'Norwegian' },
  { locale: 'pa', label: 'Punjabi' },
  { locale: 'pl', label: 'Polish' },
  { locale: 'pt', label: 'Portuguese' },
  { locale: 'ru', label: 'Russian' },
  { locale: 'sv', label: 'Swedish' },
  { locale: 'tr', label: 'Turkish' },
  { locale: 'zh', label: 'Chinese Simplified' },
  { locale: 'zh-TW', label: 'Chinese Traditional' },
];

const skEngines = [
  { token: SK_ENGINE_GENERAL },
  { token: SK_ENGINE_TRAVEL },
  { token: SK_ENGINE_FOOD },
  { token: SK_ENGINE_WEDDINGS },
  { token: SK_ENGINE_NSFW },
  { token: SK_ENGINE_CELEBRITIES },
  { token: SK_ENGINE_APPAREL },
  { token: SK_ENGINE_LOGO },
  { token: SK_ENGINE_MODERATION },
  { token: SK_ENGINE_ART_STYLE },
  { token: SK_ENGINE_ART_MOVEMENT },
  { token: SK_ENGINE_ART_SUBJECT },
];

type Props = {
  catalog: Catalog,
  getCatalogPropertiesCommand: typeof getCatalogPropertiesCommand,
  intl: IntlShape,
  smartKeywordsSettings: ?SmartKeywordsSettings,
  updateSmartKeywordsSettings: typeof updateSmartKeywordsSettings,
};

class CatalogSmartKeywordsPage extends Component<Props> {
  handleChangeEngineCheckbox: Function;
  handleChangeLanguageCheckbox: Function;
  handleChangeSlider: Function;
  handleChangeTopLevelCheckbox: Function;
  updateSettings: Function;

  constructor() {
    super();

    this.handleChangeEngineCheckbox = this.handleChangeEngineCheckbox.bind(
      this
    );
    this.handleChangeLanguageCheckbox = this.handleChangeLanguageCheckbox.bind(
      this
    );
    this.handleChangeSlider = this.handleChangeSlider.bind(this);
    this.handleChangeTopLevelCheckbox = this.handleChangeTopLevelCheckbox.bind(
      this
    );
    this.updateSettings = this.updateSettings.bind(this);
  }

  componentDidMount() {
    const { catalog } = this.props;
    if (catalog) {
      this.props.getCatalogPropertiesCommand(catalog.catalogId, [
        CATALOG_PROPERTY_KEY.SMART_KEYWORDS_SETTINGS,
        CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
      ]);
    }
  }

  handleChangeTopLevelCheckbox(e: SyntheticMouseEvent<HTMLInputElement>) {
    const { catalog, smartKeywordsSettings } = this.props;
    const { id } = e.currentTarget;

    if (smartKeywordsSettings) {
      this.props.updateSmartKeywordsSettings(catalog.catalogId, {
        ...smartKeywordsSettings,
        [id]: !smartKeywordsSettings[id],
      });
    }
  }

  handleChangeEngineCheckbox(e: SyntheticMouseEvent<HTMLInputElement>) {
    const { catalog, smartKeywordsSettings } = this.props;
    const { id, checked } = e.currentTarget;

    if (smartKeywordsSettings) {
      this.props.updateSmartKeywordsSettings(catalog.catalogId, {
        ...smartKeywordsSettings,
        engines: checked
          ? [...new Set([...smartKeywordsSettings.engines, id])]
          : smartKeywordsSettings.engines.filter(engine => engine !== id),
      });
    }
  }

  handleChangeLanguageCheckbox(e: SyntheticMouseEvent<HTMLInputElement>) {
    const { smartKeywordsSettings } = this.props;
    const { id, checked } = e.currentTarget;

    if (smartKeywordsSettings) {
      this.updateSettings({
        ...smartKeywordsSettings,
        languages: checked
          ? [...new Set([...smartKeywordsSettings.languages, id])]
          : smartKeywordsSettings.languages.filter(language => language !== id),
      });
    }
  }

  handleChangeSlider(value: number, id: string) {
    const { smartKeywordsSettings } = this.props;

    if (smartKeywordsSettings) {
      if (id === CONFIDENCE_LEVEL) {
        value /= 100;
      }

      this.updateSettings({
        ...smartKeywordsSettings,
        [id]: value,
      });
    }
  }

  updateSettings(smartKeywordsSettings: SmartKeywordsSettings) {
    const { catalog, updateSmartKeywordsSettings } = this.props;
    updateSmartKeywordsSettings(catalog.catalogId, smartKeywordsSettings);
  }

  render() {
    const {
      intl,
      intl: { formatMessage },
      smartKeywordsSettings,
    } = this.props;
    const [
      enabled,
      smartKeywordOnCataloging,
      numKeywords,
      confidenceLevel,
      languages,
    ] = (smartKeywordsSettings && [
      smartKeywordsSettings.enabled,
      smartKeywordsSettings.smartKeywordOnCataloging,
      smartKeywordsSettings.numKeywords,
      smartKeywordsSettings.confidenceLevel * 100,
      smartKeywordsSettings.languages,
    ]) || [false, false, 1, 1, []];

    return (
      <div
        className="portfolio-form-container is-textured"
        data-test-name="catalog-smart-keywords-page"
      >
        <div className="box">
          <div className="field">
            <div className="control">
              <input
                className="is-checkradio is-medium"
                data-test-name="enabled-checkbox"
                id="enabled"
                type="checkbox"
                checked={enabled}
                onChange={this.handleChangeTopLevelCheckbox}
              />
              <label htmlFor="enabled">
                <FormattedMessage
                  {...localizedStrings.CATALOG_SK_USE_SK_LABEL}
                />
              </label>
            </div>
          </div>
          <div className="field is-indented">
            <div className="control">
              <input
                className="is-checkradio"
                data-test-name="smart-keyword-on-cataloging"
                disabled={!enabled}
                id="smartKeywordOnCataloging"
                type="checkbox"
                checked={smartKeywordOnCataloging}
                onChange={this.handleChangeTopLevelCheckbox}
              />
              <label htmlFor="smartKeywordOnCataloging">
                <FormattedMessage
                  {...localizedStrings.CATALOG_SK_ON_CATALOGING_LABEL}
                />
              </label>
            </div>
          </div>
          <section className="section">
            <div className="columns">
              <div className="column is-one-third">
                <FormattedMessage
                  {...localizedStrings.CATALOG_SK_DEFAULT_SETTINGS}
                />
              </div>
              <div className="column is-one-third">
                <FormattedMessage
                  {...localizedStrings.CATALOG_SK_NUM_KEYWORDS_LABEL}
                />
                <br />
                <span className="help">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_SK_DEFAULT_LABEL}
                    values={{ recommended: '7' }}
                  />
                </span>
                <br />
                <div
                  className="vertical-spacer-small"
                  data-test-name="default-num-keywords"
                />
                <BulmaSlider
                  disabled={!enabled}
                  id={NUM_KEYWORDS}
                  initialValue={numKeywords}
                  min={1}
                  max={20}
                  onChange={this.handleChangeSlider}
                />
              </div>
              <div className="column is-one-third">
                <FormattedMessage
                  {...localizedStrings.CATALOG_SK_CONFIDENCE_LEVEL_LABEL}
                />
                <br />
                <span className="help">
                  <FormattedMessage
                    {...localizedStrings.CATALOG_SK_DEFAULT_LABEL}
                    values={{ recommended: '87' }}
                  />
                </span>
                <br />
                <div
                  className="vertical-spacer-small"
                  data-test-name="default-confidence-level"
                />
                <BulmaSlider
                  disabled={!enabled}
                  id={CONFIDENCE_LEVEL}
                  initialValue={confidenceLevel}
                  onChange={this.handleChangeSlider}
                  min={1}
                  max={100}
                />
              </div>
            </div>

            <div
              className="is-divider"
              data-content={formatMessage(
                localizedStrings.CATALOG_SK_ENGINES_LABEL
              )}
            />

            {smartKeywordsSettings &&
              skEngines.map(engine => (
                <SmartKeywordsEngineSettingsRow
                  engine={engine}
                  handleChangeEngineCheckbox={this.handleChangeEngineCheckbox}
                  intl={intl}
                  key={`engine-settings-${engine.token}`}
                  smartKeywordsSettings={smartKeywordsSettings}
                  updateSettings={this.updateSettings}
                />
              ))}
            <div
              className="is-divider"
              data-content={formatMessage(
                localizedStrings.CATALOG_SK_LANGUAGES_LABEL
              )}
            />
            <div>
              <div className="field is-grouped is-grouped-multiline">
                {skLanguages.map(language => (
                  <div className="control" key={language.locale}>
                    <input
                      className="is-checkradio is-small"
                      data-test-name={'skLocale-' + language.label}
                      disabled={!enabled}
                      id={language.locale}
                      type="checkbox"
                      checked={languages.includes(language.locale)}
                      onChange={this.handleChangeLanguageCheckbox}
                    />
                    <label htmlFor={language.locale}>{language.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    smartKeywordsSettings: getSmartKeywordsSettings(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      getCatalogPropertiesCommand,
      updateSmartKeywordsSettings,
    })
)(injectIntl(CatalogSmartKeywordsPage));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogSmartKeywordsPage.js